import dayjs from 'dayjs'
import { Controller, useFormContext } from 'react-hook-form'
import InputMask from 'react-input-mask'
import { toast } from 'react-toastify'

import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../constants/toast'
import {
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
} from '../../../services/hooks/meetings/mutations/useMeeting'
import { getErrorMessage } from '../../../utils/getErrorMessage'
import { invalidateQueriesList } from '../../../utils/invalidateQueriesList'
import { Button } from '../../Button'
import { Calendar } from '../../Calendar'
import { FormGroup } from '../../FormElements/FormGroup'

type TimeStepProps = {
  meetingId?: number
  submitButtonText: string
  taskId: number
  onGoBackToPreviousStep: () => void
  onSuccessfulSubmit: () => void
}

export const TimeStep = ({
  meetingId,
  taskId,
  submitButtonText,
  onGoBackToPreviousStep,
  onSuccessfulSubmit,
}: TimeStepProps) => {
  const queryClient = useQueryClient()

  const { mutate: createMeeting, isLoading: isCreatingMeeting } =
    useCreateMeetingMutation()
  const { mutate: updateMeeting, isLoading: isUpatingMeeting } =
    useUpdateMeetingMutation()
  const {
    control,
    formState,
    setValue,
    watch,
    handleSubmit,
    getFieldState,
    clearErrors,
  } = useFormContext()

  const [selectedDate, setSelectedDate] = useState(dayjs().toDate())

  const dateFieldValue = watch('date')
  const startTimeFieldValueWatched = watch('startTime')
  const startTimeFieldValue = getFieldState('startTime')

  useEffect(() => {
    if (startTimeFieldValue.invalid) {
      clearErrors('endTime')
      setValue('endTime', '')
    }
  }, [startTimeFieldValue.invalid])

  useEffect(() => {
    const isValidDate = /[0-9]{2}\/[0-9]{2}\/[0-9]{4}/.test(dateFieldValue)

    if (isValidDate) {
      const validDate = dateFieldValue.split('/').reverse().join('-')
      setSelectedDate(dayjs(validDate).toDate())
    }
  }, [dateFieldValue, startTimeFieldValueWatched])

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setValue('date', dayjs(date).format('DD/MM/YYYY'))
  }

  const removeDuplicatedItemsById = (
    array: Array<{ id: number, name: string, email: string }>
  ) => {
    return array.filter((item: any, index: number, array: any[]) => {
      const firstIndex = array.findIndex((subItem) => {
        // eslint-disable-next-line eqeqeq
        return subItem.id == item.id
      })

      return firstIndex === index
    })
  }

  const handleScheduleMeeting = async (formData: any) => {
    const formattedDate = formData.date.split('/').reverse().join('-')
    const startTimeArray = formData.startTime.split(':')
    const endAtTimeArray = formData.endTime.split(':')

    const date = dayjs(formattedDate)

    const startAt = date
      .set('hours', Number(startTimeArray[0]))
      .set('minutes', Number(startTimeArray[1]))
      .toISOString()

    const endAt = date
      .set('hours', Number(endAtTimeArray[0]))
      .set('minutes', Number(endAtTimeArray[1]))
      .toISOString()

    const filteredExperts = removeDuplicatedItemsById(formData.experts)
    const filteredAnalysts = removeDuplicatedItemsById(formData.analysts)
    const filteredAdministrativeUsers = removeDuplicatedItemsById(
      formData.administrativeUsers
    )
    const filteredContacts =
      formData.contacts?.length > 0
        ? removeDuplicatedItemsById(formData.contacts)
        : []

    if (meetingId) {
      updateMeeting(
        {
          id: meetingId,
          data: {
            taskId,
            summary: formData.summary,
            description: formData.description,
            startAt,
            endAt,
            experts: filteredExperts,
            analysts: filteredAnalysts,
            administrativeUsers: filteredAdministrativeUsers,
            contacts: filteredContacts,
          },
        },
        {
          onSuccess () {
            invalidateQueriesList(queryClient, [
              'payment-meeting-details',
              '@meetings/researcherNames',
              '@meetings/clientNames',
              '@meetings/expertNames',
              '@meetings/positions',
              '@meetings/currentCompanies',
              '@meetings/status',
              '@meetings/assetNames',
              '@meetings/analystNames',
            ])

            toast.success(
              'Reunião atualizada com sucesso!',
              TOAST_SUCCESS_DEFAULT_OPTIONS
            )

            onSuccessfulSubmit()
          },
          onError (error) {
            toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
          },
        }
      )
      return
    }

    createMeeting(
      {
        taskId,
        summary: formData.summary,
        description: formData.description,
        startAt,
        endAt,
        experts: filteredExperts,
        analysts: filteredAnalysts,
        administrativeUsers: filteredAdministrativeUsers,
        contacts: filteredContacts,
      },
      {
        onSuccess () {
          invalidateQueriesList(queryClient, [
            'payment-meeting-details',
            '@meetings/researcherNames',
            '@meetings/clientNames',
            '@meetings/expertNames',
            '@meetings/positions',
            '@meetings/currentCompanies',
            '@meetings/status',
            '@meetings/assetNames',
            '@meetings/analystNames',
          ])

          toast.success(
            'Reunião agendada com sucesso!',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )

          onSuccessfulSubmit()
        },
        onError (error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  return (
    <form
      className="flex flex-col items-center pr-2 max-h-[80vh] overflow-y-auto scrollbar"
      onSubmit={handleSubmit(handleScheduleMeeting)}
    >
      <div>
        <div className="max-w-xs mx-auto">
          <Calendar
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
          />
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:gap-12">
          <fieldset className="mb-6">
            <legend className="block mb-2 font-bold text-grey-800">
              Data:
            </legend>

            <Controller
              name="date"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <FormGroup error={error?.message}>
                  <InputMask
                    value={value}
                    onChange={onChange}
                    mask="99/99/9999"
                    maskPlaceholder={null}
                  >
                    <input
                      type="text"
                      placeholder="00/00/0000"
                      className="text-grey-800 w-28 text-sm rounded-[5px] p-3 outline-none border appearance-none border-grey-700"
                    />
                  </InputMask>
                </FormGroup>
              )}
            />
          </fieldset>

          <fieldset className="mb-20">
            <legend className="block mb-2 font-bold text-grey-800">
              Horário:
            </legend>

            <div className="flex items-baseline gap-2">
              <Controller
                name="startTime"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <FormGroup error={error?.message}>
                    <InputMask
                      value={value}
                      onChange={onChange}
                      mask="99:99"
                      maskPlaceholder={null}
                    >
                      <input
                        type="text"
                        placeholder="00:00"
                        className="text-grey-800 w-28 text-sm rounded-[5px] p-3 outline-none border appearance-none border-grey-700"
                      />
                    </InputMask>
                  </FormGroup>
                )}
              />

              <p>até</p>

              <Controller
                name="endTime"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <FormGroup error={error?.message}>
                    <InputMask
                      value={value}
                      onChange={onChange}
                      disabled={startTimeFieldValue.invalid}
                      mask="99:99"
                      maskPlaceholder={null}
                    >
                      <input
                        type="text"
                        placeholder="00:00"
                        className="disabled:opacity-40 text-grey-800 w-28 text-sm rounded-[5px] p-3 outline-none border appearance-none border-grey-700"
                      />
                    </InputMask>
                  </FormGroup>
                )}
              />
            </div>
          </fieldset>
        </div>

        <footer className="flex space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={onGoBackToPreviousStep}
            fullWidth
            size="large"
            className="whitespace-nowrap"
          >
            Voltar
          </Button>

          <Button
            type="submit"
            variant="primary"
            isLoading={isCreatingMeeting || isUpatingMeeting}
            disabled={!formState.isValid}
            fullWidth
            size="large"
            className="whitespace-nowrap"
          >
            {submitButtonText}
          </Button>
        </footer>
      </div>
    </form>
  )
}
